import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "./styles/globals.css";
import "./design-system/styles/global.css";
import "@/shared/config/i18n";
import { IconContext } from "@/shared/ui/icons";
import { loadRemoteFeatureFlags } from "./config/feature-flags";
import { installTranslateCrashGuard } from "@/shared/infra/translate-crash-guard";
import { initErrorReporting } from "@/shared/infra/error-reporter";
import { seedDevAuth } from "@/app/dev-auth";
import { captureReferralFromURL } from "@/shared/utils/referral-code";

// Before ANY React render: browser page-translation swaps React's text nodes,
// so the next unmount (e.g. switching symbol) throws NotFoundError and kills the
// section. See lib/translate-crash-guard.ts.
installTranslateCrashGuard();

// Window-level error listeners, installed before the app module graph loads so
// a failure during boot still reaches the ops channel.
initErrorReporting();

// Refer & earn: bank ?aff_id= before anything can navigate away from the landing
// URL, and strip it from the address bar so the visitor cannot accidentally
// share someone else's code by copying their own location. Runs on every boot;
// no-ops when there is no code in the URL.
captureReferralFromURL();

// App-wide Phosphor icon defaults (the icon set is Phosphor behind the legacy
// Lucide names — see src/lib/icons.tsx). `size: 24` matches Lucide's old default
// so icons sized only by className are unchanged; `weight: "regular"` gives the
// clean geometric outline. Bump to "bold"/"duotone" here to reskin every icon.
const ICON_DEFAULTS = { weight: "regular", size: 24 } as const;

// Fetch the admin-toggled feature flags BEFORE the app module graph loads, so
// even module-level reads of FEATURE_FLAGS (e.g. the command-palette section
// list) resolve against the server values. Fail-open + short timeout inside
// loadRemoteFeatureFlags() → a config outage never blocks boot. App is imported
// dynamically here for the same reason (its modules must evaluate after the
// flags are applied).
async function bootstrap() {
  // DEV-only: seed the persisted session from the dev-auto-login plugin BEFORE
  // the app graph loads, so AppShell's initAuthBootstrap reconciles it on boot.
  seedDevAuth();
  await loadRemoteFeatureFlags();
  const { default: App } = await import("./App");
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <IconContext.Provider value={ICON_DEFAULTS}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </IconContext.Provider>
    </StrictMode>,
  );
}

void bootstrap();
